import { CanadianProvincesWithISOCodes } from '@/utils/address';
import { Chip } from '@liveauctioneers/hammer-ui-core/chips';
import { ComponentProps, MouseEvent, useCallback, useMemo, useState } from 'react';
import { ConditionalMenuContainer } from '../ConditionalMenuContainer/ConditionalMenuContainer';
import { CurrentOpenMenu } from '@/pages/SearchPageSimplified/types';
import { Distance } from '@/types/Distance';
import { FacetOption } from '@/types/FacetOption';
import { FilterAction } from '@/types/SearchFacet';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { LocationRangeFilter } from './components/LocationRangeFilter/LocationRangeFilter';
import {
    modifyParamsRemoveCountryAndDistance,
    modifyParamsRemovePage,
    modifyParamsReplaceCountry,
    modifyParamsWithDistance,
    parseQueryParams,
} from '@/utils/queryParams';
import { QuickFilterName, useTrackQuickFilterPillClicked } from '@/utils/amplitude/analytics/searchPage';
import { Radio } from '@liveauctioneers/hammer-ui-core/radio';
import { StateFilter } from './components/StateFilter/StateFilter';
import { UpdateLocationModal } from './components/UpdateLocationModal/UpdateLocationModal';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import FacetTypes from '@/types/search/enums/facetTypes';
import paramTypes from '@/types/search/enums/paramTypes';
import USStatesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/USStatesWithISOCodes';

const PINNED_COUNTRIES = ['US', 'UK', 'CA'];

const sortByPinnedThenCount = (firstOption: FacetOption, secondOption: FacetOption) => {
    const firstPinIndex = PINNED_COUNTRIES.indexOf(firstOption.id);
    const secondPinIndex = PINNED_COUNTRIES.indexOf(secondOption.id);
    if (firstPinIndex !== -1 && secondPinIndex !== -1) {
        return firstPinIndex - secondPinIndex;
    }
    if (firstPinIndex !== -1) {
        return -1;
    }
    if (secondPinIndex !== -1) {
        return 1;
    }
    return (secondOption.count ?? 0) - (firstOption.count ?? 0);
};

type Props = {
    currentOpenMenu?: CurrentOpenMenu;
    distance: Distance;
    isAllFilters?: boolean;
    isArchivedSearch: boolean;
    onMenuClose: () => void;
    onMenuOpen: (currentMenu: CurrentOpenMenu) => void;
};

export const LocationFilter = ({
    currentOpenMenu,
    distance,
    isAllFilters,
    isArchivedSearch,
    onMenuClose,
    onMenuOpen,
}: Props) => {
    const { formatMessage } = useIntl();
    const trackQuickFilterPillClicked = useTrackQuickFilterPillClicked();
    const locationFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearch, FacetTypes.COUNTRY_CODE_ID)
    );
    const { options = [] } = locationFacet;
    const { search } = useLocation();

    const queryParams = parseQueryParams(search);
    const countryFacetParam = queryParams.country;
    const stateFacetParam = queryParams[paramTypes.STATE_ID];
    const navigateWithParams = useNavigateSearchPageWithParams();
    const showStateFilter = (countryFacetParam === 'US' || countryFacetParam === 'CA') && !distance.zipCode;

    const [showLocationModal, setShowLocationModal] = useState(false);

    const handleOpenLocationModal = useCallback(() => {
        setShowLocationModal(true);
    }, []);

    const handleWithinRadioSelect = useCallback(() => {
        if (distance?.zipCode) {
            return;
        }
        setShowLocationModal(true);
    }, [distance?.zipCode]);

    const handleLocationModalClose = useCallback(() => {
        setShowLocationModal(false);
    }, []);

    const handleLocationModalApply = useCallback(
        (zipCode: string, distanceValue: number) => {
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;
            const country = 'US';
            const measurement = 'mile';
            let newParams = modifyParamsWithDistance(queryParams, country, zipCode, String(distanceValue), measurement);
            newParams = modifyParamsRemovePage(newParams);

            navigateWithParams({
                checkForPageRemoval: true,
                currentOpenMenu: keepMenuOpenValue,
                filterAction: FilterAction.LocationChanged,
                params: newParams,
                preventScrollReset: true,
            });
            setShowLocationModal(false);
        },
        [isAllFilters, navigateWithParams, queryParams]
    );

    const handleMenuOpen: ComponentProps<typeof ConditionalMenuContainer>['onMenuOpen'] = useCallback(
        (menu) => {
            if (!isAllFilters) {
                trackQuickFilterPillClicked(QuickFilterName.Location);
            }
            onMenuOpen(menu);
        },
        [isAllFilters, onMenuOpen, trackQuickFilterPillClicked]
    );

    const selectedDistanceFacetName = useMemo(() => {
        const selectedCountryFacet = options.find((option) => option.id === countryFacetParam);

        if (distance.zipCode) {
            return formatMessage(
                { id: 'searchPage.facets.withinDistanceOf' },
                { distance: `${distance.space} miles`, location: distance.zipCode }
            );
        }

        let stateName: string | undefined;
        if (stateFacetParam) {
            const regions = countryFacetParam === 'CA' ? CanadianProvincesWithISOCodes : USStatesWithISOCodes;
            stateName = regions.find((r) => r.code === stateFacetParam)?.name;
        }

        return stateName ? `${stateName}, ${selectedCountryFacet?.name}` : selectedCountryFacet?.name;
    }, [countryFacetParam, distance.space, distance.zipCode, formatMessage, options, stateFacetParam]);

    const sortedOptions = useMemo(
        () =>
            options
                .filter((option: FacetOption) => option.count > 0 || option.id === countryFacetParam)
                .sort(sortByPinnedThenCount),
        [options, countryFacetParam]
    );

    const onCountryOptionClick = useCallback(
        (option?: FacetOption) => {
            const keepMenuOpenValue = isAllFilters ? CurrentOpenMenu.AllFilters : CurrentOpenMenu.Location;

            if (!option) {
                let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
                newParams = modifyParamsRemovePage(newParams);
                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationRemoved,
                    params: newParams,
                    preventScrollReset: true,
                });
            } else {
                let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
                newParams = modifyParamsReplaceCountry(newParams, option.id);
                newParams = modifyParamsRemovePage(newParams);
                navigateWithParams({
                    checkForPageRemoval: true,
                    currentOpenMenu: keepMenuOpenValue,
                    filterAction: FilterAction.LocationChanged,
                    params: newParams,
                    preventScrollReset: true,
                });
            }
        },
        [isAllFilters, navigateWithParams, queryParams]
    );

    const onRemoveChipCountryAndDistanceValue = useCallback(
        (event: MouseEvent<HTMLInputElement>) => {
            event.preventDefault();
            let newParams = modifyParamsRemoveCountryAndDistance(queryParams);
            newParams = modifyParamsRemovePage(newParams);
            navigateWithParams({
                checkForPageRemoval: true,
                filterAction: FilterAction.LocationRemoved,
                params: newParams,
                preventScrollReset: true,
            });
        },
        [navigateWithParams, queryParams]
    );

    return (
        <>
            <ConditionalMenuContainer
                accordionDefaultOpen
                currentOpenMenu={currentOpenMenu}
                currentOpenMenuId={CurrentOpenMenu.Location}
                isAllFilters={isAllFilters}
                onMenuClose={onMenuClose}
                onMenuOpen={handleMenuOpen}
                title={formatMessage({ id: 'searchPage.facets.itemLocation' })}
                trigger={
                    <Chip
                        onClose={onRemoveChipCountryAndDistanceValue}
                        selected={Boolean(selectedDistanceFacetName)}
                        style="singleFilter"
                    >
                        {selectedDistanceFacetName || formatMessage({ id: 'searchPage.facets.itemLocation' })}
                    </Chip>
                }
            >
                <Radio.Option
                    checked={!countryFacetParam && !distance.zipCode}
                    onChange={() => onCountryOptionClick()}
                >
                    {formatMessage({ id: 'searchPage.facets.anywhere' })}
                </Radio.Option>

                {!isArchivedSearch && (
                    <LocationRangeFilter
                        distance={distance}
                        onOpenModal={handleOpenLocationModal}
                        onRadioSelect={handleWithinRadioSelect}
                    />
                )}

                <ViewMore
                    collapseText="View Less"
                    expandText="View More"
                    fadeBackgroundColor="#fff"
                >
                    <div className="flex flex-col gap-xs">
                        {sortedOptions.map((option: FacetOption) => (
                            <div key={option.id}>
                                <Radio.Option
                                    checked={option.id === countryFacetParam && !distance.zipCode}
                                    onChange={() => onCountryOptionClick(option)}
                                >
                                    {option.name}
                                    {!distance.zipCode && (
                                        <>
                                            &nbsp;
                                            <span className="text-text-secondary">
                                                ({getRoundedNumberText({ totalFound: option.count })})
                                            </span>
                                        </>
                                    )}
                                </Radio.Option>
                                {showStateFilter && option.id === countryFacetParam && (
                                    <StateFilter
                                        countryCode={countryFacetParam}
                                        isAllFilters={isAllFilters}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                </ViewMore>
            </ConditionalMenuContainer>
            {!isArchivedSearch && (
                <UpdateLocationModal
                    distance={distance}
                    onApply={handleLocationModalApply}
                    onClose={handleLocationModalClose}
                    show={showLocationModal}
                />
            )}
        </>
    );
};
